
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AIModel, AIConfig } from "@/types/admin";
import { updateAIConfig } from "@/services/admin/aiConfigService";
import { Loader2, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ModelSelectorProps {
  models: AIModel[];
  task: 'pre-call' | 'post-call';
  initialConfig?: AIConfig | null;
  onConfigSaved?: (config: AIConfig) => void;
}

const ModelSelector = ({ models, task, initialConfig, onConfigSaved }: ModelSelectorProps) => {
  const [selectedModel, setSelectedModel] = useState<string | undefined>(initialConfig?.model_name);
  const [temperature, setTemperature] = useState<number>(initialConfig?.temperature || 0.7);
  const [topP, setTopP] = useState<number>(initialConfig?.top_p || 1);
  const [maxTokens, setMaxTokens] = useState<number>(initialConfig?.max_tokens || 4000);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState<string | undefined>(initialConfig?.model_provider);
  
  useEffect(() => {
    if (initialConfig) {
      setSelectedModel(initialConfig.model_name);
      setTemperature(initialConfig.temperature);
      setTopP(initialConfig.top_p);
      setMaxTokens(initialConfig.max_tokens);
      setProvider(initialConfig.model_provider);
    }
  }, [initialConfig]);

  const handleSaveConfig = async () => {
    if (!selectedModel || !provider) {
      toast({
        title: "Missing Information",
        description: "Please select a model before saving configuration",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      
      const config: AIConfig = {
        id: initialConfig?.id,
        model_provider: provider,
        model_name: selectedModel,
        for_task: task,
        temperature,
        top_p: topP,
        max_tokens: maxTokens
      };
      
      const result = await updateAIConfig(config);
      
      if (result.success) {
        toast({
          title: "Configuration Saved",
          description: `AI model for ${task} analysis has been updated`
        });
        
        if (onConfigSaved) {
          onConfigSaved(config);
        }
      } else {
        throw new Error(result.error || "Failed to save configuration");
      }
    } catch (error) {
      console.error("Error saving AI config:", error);
      toast({
        title: "Error Saving Configuration",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="model">AI Model</Label>
            <Select
              value={selectedModel}
              onValueChange={(value) => {
                setSelectedModel(value);
                // Find the provider for this model
                const model = models.find(m => m.id === value);
                if (model) {
                  setProvider(model.provider);
                }
              }}
            >
              <SelectTrigger id="model">
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="temperature">Temperature: {temperature.toFixed(1)}</Label>
            </div>
            <Slider
              id="temperature"
              min={0}
              max={1}
              step={0.1}
              value={[temperature]}
              onValueChange={(values) => setTemperature(values[0])}
            />
            <p className="text-xs text-muted-foreground">
              Lower values make output more focused and deterministic, higher values make it more creative
            </p>
          </div>
          
          <div className="grid gap-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="top-p">Top P: {topP.toFixed(1)}</Label>
            </div>
            <Slider
              id="top-p"
              min={0}
              max={1}
              step={0.1}
              value={[topP]}
              onValueChange={(values) => setTopP(values[0])}
            />
            <p className="text-xs text-muted-foreground">
              Controls diversity via nucleus sampling
            </p>
          </div>
          
          <div className="grid gap-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="max-tokens">Max Tokens: {maxTokens}</Label>
            </div>
            <Slider
              id="max-tokens"
              min={1000}
              max={8000}
              step={100}
              value={[maxTokens]}
              onValueChange={(values) => setMaxTokens(values[0])}
            />
            <p className="text-xs text-muted-foreground">
              Maximum length of generated content
            </p>
          </div>
          
          <Button 
            onClick={handleSaveConfig} 
            className="w-full"
            disabled={saving || !selectedModel}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Save Configuration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ModelSelector;
